'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'

import DragRail from '@/core/components/optional/drag-rail'
import { cn } from '@/core/utils/common'

import type { Route } from 'next'
import type { ComponentProps, ReactNode } from 'react'

/* 목록 화면 공용 뼈대 — 레퍼런스 .sec .sec-h .rail .tabs .seg .chips */

/**
 * 섹션 머리 — 홈 섹션 머리(feed-home H2)와 같은 크기 · 모양(제목 PC 18 · 모바일 17, 부제 13 · 12).
 * 제목 앞 기울인 코랄 막대(게임 메뉴식 장식)는 2026-10-07 리메이크에서 걷었다.
 * wide — PC 에서 본문 폭(1060)에 가두지 않고 화면 폭을 다 쓴다(홈 · 랭킹 · 라운지 · 마이, 좌우 32)
 */
export function SectionHead({
  title,
  sub,
  right,
  className,
  wide,
}: {
  title: ReactNode
  sub?: string
  right?: ReactNode
  className?: string
  wide?: boolean
}) {
  return (
    <section
      className={cn(
        'mt-6 px-4',
        wide ? 'over10:px-8' : 'over10:content-column over10:px-6.5',
        className
      )}
    >
      <div className="mb-2.5 flex items-end justify-between gap-3 over10:mb-3">
        <div className="min-w-0">
          <h3 className="truncate text-c17 leading-[1.3] font-bold tracking-[-0.01em] over10:text-c18">
            {title}
          </h3>
          {sub && (
            <p className="mt-0.5 truncate text-c12 text-t3 over10:text-c13">
              {sub}
            </p>
          )}
        </div>
        {right}
      </div>
    </section>
  )
}

/** 카드 레일 — 끌어서(PC 는 마우스로도) 넘기는 가로 목록. 첫 카드는 화면 여백에 맞춰 시작한다. wide 는 SectionHead 와 같다 */
export function Rail({
  children,
  className,
  ariaLabel,
  wide,
}: {
  children: ReactNode
  className?: string
  ariaLabel?: string
  wide?: boolean
}) {
  return (
    <DragRail
      ariaLabel={ariaLabel}
      className={cn(!wide && 'over10:content-column', className)}
      classNames={{
        track: cn(
          'gap-2.5 px-4',
          wide ? 'over10:gap-4 over10:px-8' : 'over10:px-6.5'
        ),
      }}
    >
      {children}
    </DragRail>
  )
}

type TabItem = {
  key: string
  label: string
  href?: Route
  prefetch?: boolean
  onNavigate?: ComponentProps<typeof Link>['onNavigate']
  on?: boolean
  onClick?: () => void
}

/**
 * 밑줄 탭 (.tabs) — 이동형(href) 과 동작형(onClick) 을 섞어 쓴다.
 * 머리줄 아래에 붙여 따라오게 하려면 PageHeader 의 children 으로 넣는다 — 머리줄과 같은 유리 판 한 장이 된다.
 * 2026-10-07 — 스튜디오 홈 탭과 같은 모양으로: 칸을 나눠 가운데 맞추던 것(flex-1) 대신 왼쪽부터 글자 폭만큼, 고른 탭은 흰 글자 + 코랄 밑줄(빛 번짐 없음).
 * wide 는 SectionHead 와 같다
 */
export function TabRow({
  items,
  className,
  wide,
}: {
  items: TabItem[]
  className?: string
  wide?: boolean
}) {
  const base =
    "relative flex h-12 flex-none cursor-pointer items-center text-c15 font-semibold whitespace-nowrap no-underline transition-colors duration-150 hover:text-t1 focus-visible:text-t1 over10:h-13 over10:text-c16 aria-[current=page]:cursor-default aria-[current=page]:text-t1 aria-[current=page]:after:absolute aria-[current=page]:after:inset-x-0 aria-[current=page]:after:-bottom-px aria-[current=page]:after:h-0.5 aria-[current=page]:after:rounded-full aria-[current=page]:after:bg-point aria-[current=page]:after:content-['']"
  return (
    <div
      className={cn(
        'scrollbar-none flex gap-6 overflow-x-auto border-b border-line px-4',
        wide ? 'over10:px-8' : 'over10:content-column over10:px-6.5',
        className
      )}
    >
      {items.map((item) =>
        item.href && !item.on ? (
          <Link
            key={item.key}
            href={item.href}
            prefetch={item.prefetch}
            onNavigate={item.onNavigate}
            className={cn(base, 'text-t3')}
          >
            {item.label}
          </Link>
        ) : (
          <Button
            key={item.key}
            type="button"
            aria-current={item.on ? 'page' : undefined}
            onClick={item.onClick}
            className={cn(base, 'text-t3')}
            variant="bare"
          >
            {item.label}
          </Button>
        )
      )}
    </div>
  )
}

/** 알약 두 칸 세그먼트 (.seg) */
export function Segment({
  items,
  className,
}: {
  items: { key: string; label: string; on?: boolean; onClick?: () => void }[]
  className?: string
}) {
  return (
    <div
      className={cn(
        'mx-4 flex rounded-field bg-foreground/[0.06] p-1 over10:content-column',
        className
      )}
    >
      {items.map((item) => (
        <Button
          key={item.key}
          type="button"
          aria-pressed={!!item.on}
          onClick={item.onClick}
          className={cn(
            'h-9 flex-1 cursor-pointer rounded-control text-c14 leading-none font-semibold transition-[color,background-color,box-shadow] duration-200 ease-smooth',
            item.on
              ? 'bg-foreground/15 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.12)]'
              : 'text-t3 hover:text-t1 focus-visible:text-t1'
          )}
          variant="bare"
        >
          {item.label}
        </Button>
      ))}
    </div>
  )
}

/** 칩 줄 — 넘치면 끌어서 넘긴다. 화살표는 두지 않는다(칩은 작아 가리기만 한다) */
export function ChipRow({
  children,
  className,
  id,
  wide,
}: {
  children: ReactNode
  className?: string
  id?: string
  /** PC 에서 본문 폭(1060)에 가두지 않고 화면 폭을 다 쓴다(홈) */ wide?: boolean
}) {
  return (
    <DragRail
      id={id}
      showArrows={false}
      className={cn(!wide && 'over10:content-column', className)}
      classNames={{
        track: cn('gap-2 px-4', wide ? 'over10:px-8' : 'over10:px-6.5'),
      }}
    >
      {children}
    </DragRail>
  )
}
